/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

/**
 * The shapes the Data Migration center works with.
 *
 * Two halves: the connector catalogue (static — what a source CAN be and what
 * a migration may carry) and the records kept in the repository (connections,
 * job definitions, run reports, audit entries — the same JSON-documents-under-
 * Data-Dictionary pattern the scheduled-jobs feature uses).
 */

// ── connectors ──────────────────────────────────────────────────────────

export type ConnectorCategory = 'alfresco' | 'ecm' | 'legacy' | 'file-storage' | 'cloud' | 'bulk';

/** What the catalogue knows about a connector before anyone configures it. */
export type ConnectorAvailability = 'available' | 'coming-soon';

/** One field on a connector's connection form. */
export interface ConnectorField {
  key: string;
  labelKey: string;
  type: 'text' | 'url' | 'password' | 'number' | 'select' | 'checkbox' | 'textarea';
  required?: boolean;
  /** Never written to the repository; sent only to the migration service. */
  secret?: boolean;
  options?: { value: string; labelKey: string }[];
  placeholderKey?: string;
  hintKey?: string;
}

/** What a migration through this connector can carry, stated honestly. */
export interface ConnectorCapability {
  key: string;
  /** 'yes' | 'mode' | 'limited' | 'no' — rendered as a status chip. */
  level: 'yes' | 'mode' | 'limited' | 'no';
  noteKey?: string;
}

export interface MigrationConnector {
  id: string;
  category: ConnectorCategory;
  icon: string;
  titleKey: string;
  descKey: string;
  availability: ConnectorAvailability;
  capabilities: ConnectorCapability[];
  fields: ConnectorField[];
}

// ── records stored in the repository ────────────────────────────────────

/**
 * A saved connection — deliberately holds NO secrets. Passwords, tokens and
 * keys are accepted by the form but forwarded only to the migration service;
 * the copy in Data Dictionary stores the non-secret fields so the UI can list
 * and prefill the connection.
 */
export interface MigrationConnection {
  id: string;
  name: string;
  connectorId: string;
  enabled: boolean;
  config: Record<string, string | number | boolean>;
  createdBy: string;
  createdAt: string;
  /** Set when the migration service last answered a test for it. */
  lastTest?: { ok: boolean; at: string; detail?: string };
}

export type MigrationStatus = 'draft' | 'ready' | 'running' | 'paused' | 'completed' | 'completed-warnings' | 'failed' | 'cancelled' | 'interrupted';

export type DuplicatePolicy = 'skip' | 'new' | 'new-version' | 'replace' | 'review';

export interface MigrationSelection {
  /** Source paths the user picked (exact form is connector-defined). */
  paths: string[];
  includeSubfolders: boolean;
  includeVersions: boolean;
  includePermissions: boolean;
  includeMetadata: boolean;
}

/** One cell of a mapping table — source name → destination name, or a policy. */
export interface MappingRow {
  source: string;
  target: string;
  /** 'map' | 'ignore' | 'default' | 'create' | 'review' — depends on the table. */
  action?: string;
  defaultValue?: string;
}

export interface MigrationMapping {
  folders: MappingRow[];
  types: MappingRow[];
  metadata: MappingRow[];
  users: MappingRow[];
  permissions: MappingRow[];
}

export interface MigrationStats {
  folders?: number;
  documents?: number;
  versions?: number;
  bytes?: number;
  success?: number;
  warning?: number;
  failed?: number;
  skipped?: number;
}

/** A migration definition — draft until the service runs it. */
export interface MigrationJob {
  /** Node id of the JSON file under DMS Migration/Jobs. */
  id?: string;
  name: string;
  sourceConnector: string;
  sourceConnectionId?: string;
  /** Always this repository today; kept for the day it is not. */
  destination: string;
  /** Company-Home-relative folder the migrated tree lands in. Empty = the engine's default ("Migrated"). */
  destinationPath?: string;
  /** 'standard' | 'full-fidelity' — full fidelity is a repository-level mode. */
  mode: string;
  status: MigrationStatus;
  /** Wizard step the draft reached (0-based). */
  currentStep: number;
  selection: MigrationSelection;
  mapping: MigrationMapping;
  duplicatePolicy: DuplicatePolicy;
  checksumVerification: boolean;
  statistics?: MigrationStats;
  createdBy: string;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
  /** Optional recurrence — the engine's scheduler fires the job on this cron.
   * Disabled or absent means manual-only. */
  schedule?: MigrationSchedule;
}

/** Cron + timezone a migration job fires on — same shape the scheduled batch jobs use. */
export interface MigrationSchedule {
  enabled: boolean;
  /** Five-field cron (minute hour day month weekday); a sixth leading field is seconds. */
  cron: string;
  /** IANA/Windows timezone id the cron is read in. */
  timezone: string;
}

export const DEFAULT_MIGRATION_SCHEDULE: MigrationSchedule = {
  enabled: false,
  cron: '0 0 2 * * *',
  timezone: 'Asia/Bangkok'
};

/** What the migration service reports after a run — mirrors the jobs reports. */
export interface MigrationRun {
  nodeId?: string;
  jobId: string;
  jobName?: string;
  status: MigrationStatus;
  startedAt: string;
  finishedAt?: string;
  durationMs?: number;
  statistics?: MigrationStats;
  errors?: MigrationError[];
}

export interface MigrationError {
  item: string;
  sourcePath?: string;
  code?: string;
  message: string;
  attempts?: number;
  lastAttempt?: string;
}

/** Slim per-run line the engine appends to Runs/index.json — overviews read this
 * one file instead of downloading every full report. */
export interface MigrationRunIndexEntry {
  jobId: string;
  jobName?: string;
  /** Node id of the full report — fetched only when per-item detail is needed. */
  nodeId: string;
  status: string;
  startedAt?: string;
  finishedAt?: string;
  durationMs?: number;
  statistics?: MigrationStats;
  /** Count of report errors — zero means the full file is never fetched. */
  errors?: number;
}

/** One line in the migration audit trail (who did what to which job). */
export interface MigrationAuditEntry {
  at: string;
  user: string;
  action: string;
  jobId?: string;
  connectionId?: string;
  detail?: string;
  result?: string;
}

/** The deployment-wide migration settings (Data Dictionary/DMS Migration). */
export interface MigrationSettings {
  batchSize: number;
  concurrentWorkers: number;
  retryAttempts: number;
  retryDelaySec: number;
  duplicatePolicy: DuplicatePolicy;
  checksumVerification: boolean;
  preserveTimestamps: boolean;
  preserveVersions: boolean;
  preservePermissions: boolean;
  logLevel: 'minimal' | 'normal' | 'verbose';
  /** Comma-separated recipients mailed when a run finishes (engine-side SMTP). */
  notifyEmail: string;
  /** Also mail the person who started the run. */
  notifyRunOwner: boolean;
}

export const DEFAULT_MIGRATION_SETTINGS: MigrationSettings = {
  batchSize: 100,
  concurrentWorkers: 2,
  retryAttempts: 3,
  retryDelaySec: 30,
  duplicatePolicy: 'skip',
  checksumVerification: true,
  preserveTimestamps: true,
  preserveVersions: true,
  preservePermissions: true,
  logLevel: 'normal',
  notifyEmail: '',
  notifyRunOwner: false
};

/** Progress counters a running job reports (backend-written). */
export interface MigrationProgress {
  documentsDone: number;
  documentsTotal: number;
  foldersDone: number;
  foldersTotal: number;
  versionsDone: number;
  versionsTotal: number;
  bytesDone: number;
  bytesTotal: number;
}

/** The empty mapping set a fresh draft starts with. */
export function emptyMapping(): MigrationMapping {
  return { folders: [], types: [], metadata: [], users: [], permissions: [] };
}

export function emptySelection(): MigrationSelection {
  return { paths: [], includeSubfolders: true, includeVersions: true, includePermissions: false, includeMetadata: true };
}
